import { formatCurrency, getCurrencyMeta, buildCurrencyOptions, CurrencyMeta, CurrencyOptions } from "./currency";

export interface PriceableItem {
    country_code?: string;
    countryCode?: string;
    country?: {
        code?: string;
        iso2?: string;
        iso3?: string;
        iso?: string;
        currency?: string;
        currency_code?: string;
        currencyCode?: string;
        iso_code?: string;
    };
    location?: {
        country_code?: string;
        countryCode?: string;
    };
    pitch?: {
        country_code?: string;
        countryCode?: string;
    };
    currency?: string;
    currency_code?: string;
    currencyCode?: string;
    price_currency?: string;
    payin_currency?: string;
    payment_currency?: string;
    preferred_currency?: string;
    metadata?: {
        currency?: string;
        payin_currency?: string;
    };
    price?: number | string;
    wallet?: {
        currency?: string;
        country_code?: string;
    };
    wallet_currency?: string;
    wallet_balance?: number | string;
    balance?: number | string;
    default_currency?: string;
    [key: string]: unknown;
}

const pickFirstString = (...values: (string | null | undefined)[]): string => {
    for (const value of values) {
        if (typeof value === "string" && value.trim().length > 0) {
            return value.trim();
        }
    }
    return "";
};

const extractCountryCode = (item: PriceableItem | null | undefined, fallbackCountryCode?: string): string => {
    if (!item) {
        return pickFirstString(fallbackCountryCode);
    }

    const candidate = pickFirstString(
        item.country_code,
        item.countryCode,
        item.country?.code,
        item.country?.iso2,
        item.country?.iso3,
        item.country?.iso,
        item.location?.country_code,
        item.location?.countryCode,
        item.pitch?.country_code,
        item.pitch?.countryCode,
        fallbackCountryCode
    );

    if (candidate.length === 3) {
        return candidate.toUpperCase();
    }

    if (candidate.length === 2) {
        return candidate.toUpperCase();
    }

    return "";
};

const extractCurrencyCode = (item: PriceableItem | null | undefined): string => {
    if (!item) {
        return "";
    }

    return pickFirstString(
        item.currency,
        item.currency_code,
        item.currencyCode,
        item.price_currency,
        item.payin_currency,
        item.payment_currency,
        item.preferred_currency,
        item.country?.currency,
        item.country?.currency_code,
        item.country?.currencyCode,
        item.metadata?.currency,
        item.metadata?.payin_currency
    ).toUpperCase();
};

export const resolveCurrencyDetails = (item: PriceableItem | null | undefined, fallbackCountryCode?: string): CurrencyOptions => {
    const currencyCode = extractCurrencyCode(item);
    const countryCode = extractCountryCode(item, fallbackCountryCode);

    return buildCurrencyOptions(currencyCode, countryCode);
};

export interface FormatItemPriceOptions extends CurrencyOptions {
    amountSelector?: (item: PriceableItem) => number | string;
}

export const formatItemPrice = (item: PriceableItem | null | undefined, fallbackCountryCode?: string, options: FormatItemPriceOptions = {}): string => {
    if (!item) {
        return "";
    }

    const amount =
        typeof options.amountSelector === "function"
            ? options.amountSelector(item)
            : item.price;

    const numericAmount = Number(amount ?? 0);
    const { currencyCode, countryCode } = resolveCurrencyDetails(
        item,
        fallbackCountryCode
    );

    return formatCurrency(numericAmount, { currencyCode, countryCode });
};

export const formatPriceValue = (
    amount: number | string,
    { currencyCode, countryCode }: CurrencyOptions = {}
): string => {
    return formatCurrency(amount, { currencyCode, countryCode });
};

export const getCurrencyMetaFromItem = (item: PriceableItem | null | undefined, fallbackCountryCode?: string): CurrencyMeta => {
    const { currencyCode, countryCode } = resolveCurrencyDetails(
        item,
        fallbackCountryCode
    );

    return getCurrencyMeta({ currencyCode, countryCode });
};

const extractUserCurrencyMeta = (userProfile: PriceableItem | null | undefined, fallbackCountryCode?: string): CurrencyOptions => {
    if (!userProfile) {
        return buildCurrencyOptions(undefined, fallbackCountryCode || undefined);
    }

    const currencyCandidate = pickFirstString(
        userProfile.wallet?.currency,
        userProfile.wallet_currency,
        userProfile.currency,
        userProfile.preferred_currency,
        userProfile.default_currency,
        userProfile.country?.currency,
        userProfile.country?.currency_code,
        userProfile.country?.iso_code as string
    );

    const countryCandidate = pickFirstString(
        userProfile.wallet?.country_code,
        userProfile.country_code,
        userProfile.countryCode,
        userProfile.country?.code,
        userProfile.country?.iso2,
        userProfile.country?.iso3,
        userProfile.country?.iso_code as string,
        fallbackCountryCode
    );

    return buildCurrencyOptions(
        currencyCandidate ? currencyCandidate.toUpperCase() : undefined,
        countryCandidate ? countryCandidate.toUpperCase() : undefined
    );
};

export const resolveUserCurrencyDetails = (
    userProfile: PriceableItem | null | undefined,
    fallbackCountryCode?: string
): CurrencyOptions => {
    const { currencyCode, countryCode } = extractUserCurrencyMeta(
        userProfile,
        fallbackCountryCode
    );

    return buildCurrencyOptions(currencyCode, countryCode);
};

export const formatUserBalance = (
    amount: number | string,
    userProfile: PriceableItem | null | undefined,
    fallbackCountryCode?: string
): string => {
    const { currencyCode, countryCode } = resolveUserCurrencyDetails(
        userProfile,
        fallbackCountryCode
    );

    return formatCurrency(amount, { currencyCode, countryCode });
};
